import type { VotingQuestionWithResults } from '../../api/DigiobcanApi';
import { resultsDenominator } from '../../lib/votings';
import { __, getVotesDeclension } from '../../utils';
import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Option = VotingQuestionWithResults['options'][number];

type Props = {
  question: VotingQuestionWithResults;
  votesCount: number;
  /**
   * Občianska podoba je tichá (čierny pruh na vlastnú odpoveď, sivý na
   * ostatné) a počty píše slovom; úradnícka je modrá a číselná - desatina
   * percenta a monospace počet, aby sa stĺpec dal prečítať zvisle.
   */
  tone: 'citizen' | 'admin';
  /** Možnosti, ktoré tento účet označil. Kreslí sa pri nich štítok „váš hlas". */
  myOptionIds?: ReadonlySet<number>;
  /** Odkaz pod možnosťou „Iné" - občan ním otvára otvorené odpovede. */
  renderOptionFooter?: (option: Option) => React.ReactNode;
};

/**
 * Vodorovné pruhy s percentom aj počtom. Nahrádzajú výsečový graf.
 *
 * Výsečový graf mal tri chyby, ktoré pruh nemá: pri otázke s viacerými
 * odpoveďami sa jeho výseky **nesčítajú do celku** (jeden človek označí tri
 * možnosti), krátke názvy možností sa doň nezmestia a poradie sa v ňom nedá
 * prečítať bez legendy. Pruhy sú zoradené zostupne, takže poradie je prvé, čo
 * z nich vidno.
 *
 * Menovateľ percenta je preto pri každom type iný - drží to `resultsDenominator`.
 */
const ResultBars: React.FC<Props> = ({
  question,
  votesCount,
  tone,
  myOptionIds,
  renderOptionFooter,
}) => {
  const denominator = resultsDenominator(question, votesCount);

  const rows = React.useMemo(
    () =>
      [...question.options]
        .sort((a, b) => b.votes_count - a.votes_count)
        .map((option) => ({
          option,
          share: denominator ? (option.votes_count / denominator) * 100 : 0,
        })),
    [question.options, denominator],
  );

  return (
    <Stack gap={1.75}>
      {rows.map(({ option, share }) => {
        const isMine = myOptionIds?.has(option.option_id) ?? false;
        // Silný krok je „väčšinová" odpoveď; keď vieme, čo označil tento účet,
        // vyhráva to - vlastný hlas má byť v grafe nájditeľný na prvý pohľad.
        const isStrong = myOptionIds ? isMine : share >= 50;
        const barColor =
          tone === 'admin'
            ? isStrong
              ? 'blue.dark'
              : 'blue.light'
            : isStrong
              ? 'grey.800'
              : 'grey.400';

        return (
          <Stack key={option.option_id} gap={0.75}>
            <Stack
              direction='row'
              alignItems='baseline'
              gap={1.5}
              sx={{ minWidth: 0 }}
            >
              <Typography
                variant='body1'
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 15,
                  fontWeight: isStrong ? 500 : 400,
                }}
              >
                {option.type === 'free'
                  ? __('Iné (otvorená odpoveď)')
                  : option.title}
              </Typography>

              {isMine && (
                <Box
                  component='span'
                  sx={({ palette, shape }) => ({
                    flex: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 22,
                    px: 0.875,
                    borderRadius: `${shape.borderRadiusSm}px`,
                    backgroundColor: palette.secondary.main,
                    border: `1px solid ${palette.secondary.dark}`,
                    color: palette.yellow.surfaceText,
                    fontSize: 11,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  })}
                >
                  {__('váš hlas')}
                </Box>
              )}

              <Typography
                component='b'
                sx={{
                  flex: 'none',
                  width: tone === 'admin' ? 56 : 48,
                  textAlign: 'right',
                  fontSize: 15,
                  fontWeight: 700,
                }}
              >
                {tone === 'admin'
                  ? `${share.toFixed(1).replace('.', ',')} %`
                  : `${Math.round(share)} %`}
              </Typography>

              <Typography
                sx={({ typography }) => ({
                  flex: 'none',
                  width: tone === 'admin' ? 56 : 80,
                  textAlign: 'right',
                  fontSize: 14,
                  color: 'text.secondary',
                  ...(tone === 'admin' && {
                    fontFamily: typography.fontFamilyMono,
                  }),
                })}
              >
                {tone === 'admin'
                  ? option.votes_count
                  : `${option.votes_count} ${getVotesDeclension(option.votes_count)}`}
              </Typography>
            </Stack>

            <Box
              sx={({ palette, shape }) => ({
                height: 10,
                borderRadius: `${shape.borderRadiusFull}px`,
                backgroundColor: palette.grey[100],
                overflow: 'hidden',
              })}
            >
              <Box
                sx={{
                  width: `${Math.min(100, share)}%`,
                  height: 10,
                  backgroundColor: barColor,
                }}
              />
            </Box>

            {renderOptionFooter?.(option)}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ResultBars;
